import { ChangeEvent, ReactElement } from 'react';
import { BaseInput } from 'lib/BaseInput';
import validate from 'utils/validationRules';
import { InputType } from 'components/common/types/Input.types';
import { FieldValidity, InputValidationConfig, InputWithValidationProps } from './InputWithValidation.types';

const InputWithValidation = ({
  inputConfig,
  onChange = () => undefined,
  onBlur = () => undefined,
  dataTestId = '',
}: InputWithValidationProps): ReactElement => {
  const changeHandler = (event: ChangeEvent<HTMLInputElement>): void => {
    const rules: Record<string, boolean> = Object.fromEntries(
      Object.entries(inputConfig.validations).map(([key, item]) => [key, item.value]),
    );

    const { isValid, errors }: FieldValidity = validate(event.target.value, rules);

    const validations = Object.fromEntries(
      Object.entries(inputConfig.validations).map(([key, item]) => [
        key,
        { ...item, error: errors[key as keyof FieldValidity['errors']] },
      ]),
    ) as InputValidationConfig['validations'];

    const changedConfig: InputValidationConfig = {
      ...inputConfig,
      elementConfig: { ...inputConfig.elementConfig },
      value: event.target.value,
      valid: isValid,
      validations,
      touched: true,
    };

    onChange(event, changedConfig, errors);
  };

  const getInputHeader = (): ReactElement | null =>
    inputConfig.label && inputConfig.label.length ? (
      <span className="sub-header-base form-label">{inputConfig.label}</span>
    ) : null;

  const getInputElement = (): ReactElement => (
    <BaseInput
      {...inputConfig.elementConfig} // eslint-disable-line react/jsx-props-no-spreading
      type={inputConfig.elementConfig.type as InputType}
      data-test-id={dataTestId}
      value={inputConfig.value}
      onChange={changeHandler}
      onBlur={onBlur}
    />
  );

  const getMessages = (): ReactElement[] => {
    if (inputConfig.touched && !inputConfig.valid) {
      const messages: ReactElement[] = [];
      Object.entries(inputConfig.validations).forEach(([key, validation]) => {
        if (validation.error) {
          messages.push(
            <span key={key} className="text-danger body-sm">
              {validation.message}
            </span>,
          );
        }
      });
      return messages;
    }
    return [];
  };

  return (
    <div className="mb-initial w-initial">
      {getInputHeader()}
      {getInputElement()}
      {getMessages()}
    </div>
  );
};

export default InputWithValidation;
